import type { TelemetryDataPoint } from "@/types";

export interface TimelineRange {
  start: number;
  end: number;
}

type VideoCursorPosition = "before" | "inside" | "after" | "unknown";

export interface VideoCursorState {
  playbackTime: number;
  position: VideoCursorPosition;
  isOutsideRange: boolean;
}

const RANGE_EPSILON_SECONDS = 0.05;

const isFiniteNumber = (value: number | null | undefined): value is number =>
  typeof value === "number" && Number.isFinite(value);

export const normalizeTimelineRange = (
  start: number | null | undefined,
  end: number | null | undefined,
): TimelineRange | null => {
  if (!isFiniteNumber(start) || !isFiniteNumber(end)) {
    return null;
  }

  if (end < start) {
    return { start: end, end: start };
  }

  return { start, end };
};

export const getTelemetryDataRange = (
  data: TelemetryDataPoint[],
): TimelineRange | null => {
  if (data.length === 0) {
    return null;
  }

  let start = Number.POSITIVE_INFINITY;
  let end = Number.NEGATIVE_INFINITY;

  data.forEach((point) => {
    if (!isFiniteNumber(point.timestamp)) {
      return;
    }

    start = Math.min(start, point.timestamp);
    end = Math.max(end, point.timestamp);
  });

  return normalizeTimelineRange(start, end);
};

const getVideoTimelineRange = (
  durationSeconds: number | null | undefined,
): TimelineRange | null => {
  if (!isFiniteNumber(durationSeconds) || durationSeconds <= 0) {
    return null;
  }

  return { start: 0, end: durationSeconds };
};

export const getSharedTimelineRange = ({
  videoDurationSeconds,
  videoRange,
  telemetryRange,
  syncOffsetSeconds,
}: {
  videoDurationSeconds: number | null | undefined;
  videoRange?: TimelineRange | null;
  telemetryRange: TimelineRange | null;
  syncOffsetSeconds: number;
}): TimelineRange | null => {
  const ranges: TimelineRange[] = [];
  const resolvedVideoRange =
    videoRange ?? getVideoTimelineRange(videoDurationSeconds);

  if (resolvedVideoRange) {
    ranges.push(resolvedVideoRange);
  }

  if (telemetryRange) {
    ranges.push({
      start: telemetryRange.start - syncOffsetSeconds,
      end: telemetryRange.end - syncOffsetSeconds,
    });
  }

  if (ranges.length === 0) {
    return null;
  }

  return ranges.reduce<TimelineRange>(
    (combined, range) => ({
      start: Math.min(combined.start, range.start),
      end: Math.max(combined.end, range.end),
    }),
    ranges[0],
  );
};

export const clampToTimelineRange = (
  time: number,
  range: TimelineRange | null,
): number => {
  if (!isFiniteNumber(time)) {
    return range?.start ?? 0;
  }

  if (!range) {
    return Math.max(0, time);
  }

  return Math.min(Math.max(time, range.start), range.end);
};

export const getVideoCursorState = (
  cursorTime: number,
  durationSeconds: number | null | undefined,
): VideoCursorState => {
  const videoRange = getVideoTimelineRange(durationSeconds);

  if (!videoRange) {
    return {
      playbackTime: Math.max(0, cursorTime),
      position: "unknown",
      isOutsideRange: false,
    };
  }

  if (cursorTime < videoRange.start - RANGE_EPSILON_SECONDS) {
    return {
      playbackTime: videoRange.start,
      position: "before",
      isOutsideRange: true,
    };
  }

  if (cursorTime > videoRange.end + RANGE_EPSILON_SECONDS) {
    return {
      playbackTime: videoRange.end,
      position: "after",
      isOutsideRange: true,
    };
  }

  return {
    playbackTime: clampToTimelineRange(cursorTime, videoRange),
    position: "inside",
    isOutsideRange: false,
  };
};
